import {z} from "zod/v4-mini"
import * as legacyZod from "zod"
import {MaterialNode} from "#material/nodes/base/material/material-node"

export const materialNodeSchema = z.instanceof(MaterialNode as any, {message: "Expected a material node"})
export const legacyMaterialNodeSchema = legacyZod.instanceof(MaterialNode as any, {message: "Expected a material node"})

export type DynamicKeyConfig = {
    regex: RegExp
    schema: z.ZodMiniType
}

export const dynamicKeyConfigsOf = (schema: unknown): readonly DynamicKeyConfig[] =>
    (schema as {dynamicKeyConfigs?: DynamicKeyConfig[]})?.dynamicKeyConfigs ?? []

export const toStrictSchemaWithDynamicParameters = <T extends z.ZodMiniObject>(baseSchema: T, dynamicKeyConfigs: DynamicKeyConfig[]) => {
    const schema = z.pipe(
        z.record(z.string(), z.any()),
        z.transform((obj, ctx) => {
            const result: Record<string, unknown> = {}

            const staticResult = baseSchema.safeParse(obj)
            if (!staticResult.success) {
                for (const issue of staticResult.error.issues) {
                    ctx.issues.push(issue)
                }
            } else {
                Object.assign(result, staticResult.data)
            }

            for (const [key, value] of Object.entries(obj)) {
                if (key in baseSchema.shape) continue
                if (value === undefined) continue

                const config = dynamicKeyConfigs.find((c) => c.regex.test(key))

                if (config) {
                    const parsed = config.schema.safeParse(value)
                    if (parsed.success) {
                        result[key] = parsed.data
                    } else {
                        ctx.issues.push({
                            code: "custom",
                            message: `Key "${key}" must match schema: ${parsed.error.issues.map((e) => e.message).join(", ")}`,
                            input: value,
                        })
                    }
                } else {
                    ctx.issues.push({
                        code: "custom",
                        message: `Unknown key "${key}" not allowed`,
                        input: value,
                    })
                }
            }

            return result
        }),
    )
    // typed as the base object, so whatever reads the keys of a node's settings has to find them here too
    return Object.assign(schema, {shape: baseSchema.shape, dynamicKeyConfigs}) as unknown as T
}
